// ─────────────────────────────────────────────────────────────────────────────
// BookingsPanel.tsx — BOOKED CONSULTATIONS, AND THE DECK EACH ONE NEEDS.
//
// Owner ruling 2026-09-29: "When the call gate records a booked consultation, that
// company is ready for its real deck. The owner presses to build it — the same fill as
// the rehearsal, with the fetch for his prices and Google reviews. The deck is filed as
// that company's, with an approval state — draft until the owner approves it."
//
// One row per booking, soonest first: company · booked for · deck · the press.
// The DECK word is the store's (none · draft vN · approved vN); a build in flight is
// the build's own status, polled while it runs — never painted from the board.
// Build states its cost before the press: one seat session and one Google call.
// Nothing here sends, presents or charges.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MC } from '../missioncontrol/theme';
import {
  Booking, BuildStatus, approveDeck, buildDeck, normalizeBookings, openDeck, readBuildStatus,
} from '../../api/deltaDeck';

export const BUILD_COST = 'one seat session + one Google call';
const POLL_MS = 4000;

export interface BookingsPanelProps {
  /** The board's `consultation_bookings` slot, as served. */
  slot: any;
  /** The board's refresh — a filed deck or an approval moves the list at once. */
  refresh?: () => void;
  testId?: string;
}

function deckWord(b: Booking): string {
  if (b.deck === 'none') return 'no deck';
  return `${b.deck} v${b.deckVersion || '?'}`;
}

export default function BookingsPanel({ slot, refresh, testId = 'delta-bookings' }: BookingsPanelProps) {
  const { phase, rows } = normalizeBookings(slot);
  const [status, setStatus] = useState<Record<string, BuildStatus | null>>({});
  const [said, setSaid] = useState<Record<string, string | null>>({});
  const [confirming, setConfirming] = useState<string | null>(null);
  const timers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const poll = useCallback((companyId: string) => {
    if (timers.current[companyId]) return;
    timers.current[companyId] = setInterval(async () => {
      const st = await readBuildStatus(companyId);
      setStatus((s) => ({ ...s, [companyId]: st }));
      if (!st || st.state !== 'building') {
        clearInterval(timers.current[companyId]);
        delete timers.current[companyId];
        if (refresh) refresh();
      }
    }, POLL_MS);
  }, [refresh]);

  useEffect(() => () => { Object.values(timers.current).forEach(clearInterval); }, []);

  const onBuild = async (b: Booking) => {
    setConfirming(null);
    setSaid((s) => ({ ...s, [b.companyId]: null }));
    const out = await buildDeck(b.companyId);
    if (!out.ok) { setSaid((s) => ({ ...s, [b.companyId]: out.refused })); return; }
    setStatus((s) => ({ ...s, [b.companyId]: { state: 'building', version: null, why: null, failedBars: [] } }));
    poll(b.companyId);
  };

  const onApprove = async (b: Booking) => {
    if (!b.deckVersion) return;
    const out = await approveDeck(b.companyId, b.deckVersion);
    setSaid((s) => ({ ...s, [b.companyId]: out.ok ? null : out.refused }));
    if (out.ok && refresh) refresh();
  };

  const onOpen = async (b: Booking) => {
    const err = await openDeck(b.companyId, `${b.company} - deck v${b.deckVersion || ''}.pptx`);
    setSaid((s) => ({ ...s, [b.companyId]: err }));
  };

  const cell: React.CSSProperties = { padding: '8px 10px', borderBottom: `1px solid ${MC.line2}`, color: MC.text, fontSize: 13 };
  const btn = (live: boolean): React.CSSProperties => ({
    background: live ? MC.panel3 : 'transparent', color: live ? MC.teal : MC.dim,
    border: `1px solid ${live ? MC.line : MC.line2}`, borderRadius: 10, padding: '4px 10px',
    fontSize: 11, letterSpacing: '.06em', cursor: live ? 'pointer' : 'default', marginRight: 6,
  });

  return (
    <section data-testid={testId} style={{
      background: MC.panel, borderRadius: MC.radius, boxShadow: MC.cardShadow,
      padding: '16px 18px', margin: '0 0 14px 0',
    }}>
      <div style={{ color: MC.text, fontSize: 15, marginBottom: 10 }}>Booked consultations</div>
      {phase === 'unread' && <div data-testid={`${testId}-unread`} style={{ color: MC.dim, fontSize: 12 }}>not read</div>}
      {phase === 'read' && rows.length === 0 && (
        <div data-testid={`${testId}-empty`} style={{ color: MC.muted, fontSize: 12 }}>No consultations booked.</div>
      )}
      {phase === 'read' && rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Company', 'Booked for', 'Deck', ''].map((h) => (
              <th key={h} style={{ ...cell, color: MC.dim, fontSize: 11, textAlign: 'left', fontWeight: 400 }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((b) => {
              const st = status[b.companyId];
              const building = !!st && st.state === 'building';
              const note = said[b.companyId]
                || (st && st.state === 'failed' ? `failed: ${st.failedBars.join(', ')}` : null)
                || (st && (st.state === 'refused' || st.state === 'error') ? st.why : null);
              return (
                <tr key={b.companyId} data-testid={`${testId}-row-${b.companyId}`}>
                  <td style={cell}>{b.company}</td>
                  <td style={cell}>{b.bookedFor}</td>
                  <td style={{ ...cell, color: b.deck === 'approved' ? MC.green : b.deck === 'draft' ? MC.amber : MC.muted }}
                      data-testid={`${testId}-deck-${b.companyId}`}>
                    {building ? 'building…' : deckWord(b)}
                  </td>
                  <td style={cell}>
                    {confirming === b.companyId ? (
                      <>
                        <button type="button" style={btn(true)} data-testid={`${testId}-confirm-${b.companyId}`}
                                onClick={() => onBuild(b)}>BUILD · {BUILD_COST}</button>
                        <button type="button" style={btn(true)} onClick={() => setConfirming(null)}>CANCEL</button>
                      </>
                    ) : (
                      <button type="button" style={btn(!building)} disabled={building}
                              data-testid={`${testId}-build-${b.companyId}`}
                              onClick={() => setConfirming(b.companyId)}>
                        {b.deck === 'none' ? 'BUILD DECK' : 'REBUILD'}
                      </button>
                    )}
                    {b.deck !== 'none' && (
                      <button type="button" style={btn(true)} data-testid={`${testId}-open-${b.companyId}`}
                              onClick={() => onOpen(b)}>OPEN</button>
                    )}
                    {b.deck === 'draft' && !building && (
                      <button type="button" style={btn(true)} data-testid={`${testId}-approve-${b.companyId}`}
                              onClick={() => onApprove(b)}>APPROVE</button>
                    )}
                    {note && <span data-testid={`${testId}-note-${b.companyId}`}
                                   style={{ color: MC.orange, fontSize: 11 }}>{note}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
